import { useTranslation } from "react-i18next";
import {
  addSetRow,
  appendPlanToDay,
  copySlotCount,
  copySlotsToDays,
  dayRoleOf,
  daysOverCap,
  droppedRoundsOf,
  importedSlotCount,
  importSessionSlots,
  MAX_DAY_EXERCISES,
  moveSlot,
  pickAuthoredText,
  removeSetRow,
  nextSlotJoinCounts,
  removeSlot,
  renameGroup,
  seedKindOf,
  seedSlotForRole,
  setSlotRoundActive,
  updateSlot,
  updateSlotInstructions,
  type BlockSetRow,
  type Draft,
  type IntensityKind,
  type Plan,
  type Program,
  type UnitsPreference,
  programRoundLabel,
} from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import { applyLoggerAction } from "../ProgramComposer/prescribe/prescribeDispatch";
import {
  appendExercises,
  applyRoundRowPatch,
  setSlotAxis,
  supersetWithNext,
} from "./roundEdit";
import type { ProgramEditorV3Props } from "./types";
import { useRoundSetWrites } from "./useRoundSetWrites";
import type { V3Navigation } from "./useV3Navigation";

/** "D1, D3": the days a toast names, in day order. */
const dayCodes = (days: readonly number[]) =>
  [...days].sort((a, b) => a - b).map((i) => `D${i + 1}`).join(", ");

type Args = Pick<
  ProgramEditorV3Props,
  | "draft"
  | "setDraft"
  | "confirm"
  | "showToast"
  | "onUndo"
  | "resolveName"
> &
  // Optional: without them every add seeds an accessory on the RIR scale.
  Partial<Pick<ProgramEditorV3Props, "exerciseCatalog" | "profile">> & { nav: V3Navigation };

// The exercise writes of the day and round views (#336): add (to one day or
// several), copy to other days, import a saved workout or another program's
// day, replace, move, remove, superset (pair, rename, dissolve), which
// rounds the exercise runs in, note, and the set table of one round. Every one goes
// through the composer's setDraft, so each is one undo step.
export function useV3ExerciseActions({
  draft,
  setDraft,
  confirm,
  showToast,
  onUndo,
  resolveName,
  exerciseCatalog,
  profile,
  nav,
}: Args) {
  const { t } = useTranslation();
  const lang = useLocale();
  const undo = { label: t("program.undo", { defaultValue: "Undo" }), onPress: onUndo };
  const sIdx = nav.dayIdx;
  const roundSets = useRoundSetWrites({ draft, setDraft, showToast, sIdx, undo });

  // A day the write would take past MAX_DAY_EXERCISES is skipped, and said.
  const fullNote = (full: readonly number[]) =>
    t("editorV3.daysFull", {
      count: full.length,
      days: dayCodes(full),
      max: MAX_DAY_EXERCISES,
      defaultValue: "{{days}} would pass {{max}} exercises: nothing added there.",
    });

  // The picker's «In which days» (flow 2): the same picks at the end of each
  // day, in one setDraft, so one Undo takes them all back. Each exercise
  // starts with the values of ITS day's role (dayRoleOf, seedSlotForRole),
  // on the athlete's effort scale. A day that has one of them already gets it
  // again. The toast names the days, says so, and says when a role applied;
  // a plain add to the open day says nothing, as before.
  const add = (slugs: string[], days: readonly number[] = [sIdx]) => {
    const asked = days.length > 0 ? days : [sIdx];
    const full = daysOverCap(draft, asked, slugs.length);
    const targets = asked.filter((day) => !full.includes(day));
    if (targets.length === 0) return showToast(fullNote(full));
    const scale = profile?.effortScale ?? "rir";
    const kinds = slugs.map((id) => seedKindOf(exerciseCatalog?.find((e) => e.slug === id)));
    const picksFor = (d: Draft, day: number) => {
      const role = dayRoleOf(d.sessions[day] ?? { name: "" });
      return slugs.map((id, i) => ({
        exerciseId: id,
        exerciseName: resolveName(id),
        setRows: seedSlotForRole(role, kinds[i]!, d.cycleWeeks, scale, d.roundTypes),
      }));
    };
    setDraft((d) => targets.reduce((acc, day) => appendExercises(acc, day, picksFor(acc, day)), d));
    const again = targets.filter((day) =>
      (draft.sessions[day]?.slots ?? []).some((s) => slugs.includes(s.exerciseId)),
    );
    const roled = targets.some((day) => dayRoleOf(draft.sessions[day] ?? { name: "" }) !== null);
    const quiet = full.length === 0 && !roled && again.length === 0;
    if (quiet && targets.length === 1 && targets[0] === sIdx) return;
    const where = dayCodes(targets);
    const parts = [
      slugs.length === 1
        ? t("editorV3.addedOneToDays", {
            name: resolveName(slugs[0]!),
            days: where,
            defaultValue: "{{name}} added to {{days}}.",
          })
        : t("editorV3.addedManyToDays", {
            count: slugs.length,
            days: where,
            defaultValue: "{{count}} exercises added to {{days}}.",
          }),
      roled ? t("editorV3.valuesFromRole", { defaultValue: "Values from the day's role." }) : null,
      again.length > 0
        ? t("editorV3.addedAgain", {
            count: slugs.length,
            days: dayCodes(again),
            defaultValue: "{{days}} already had it: now it is there twice.",
          })
        : null,
      full.length > 0 ? fullNote(full) : null,
    ];
    showToast(parts.filter(Boolean).join(" "), undo);
  };

  // «Copy to other days», of one exercise or of the day's selection: each
  // target day gets its own copy at the end (copySlotsToDays), with Undo.
  const copyToDays = (slotIds: readonly string[], toDays: readonly number[]) => {
    if (slotIds.length === 0 || toDays.length === 0) return;
    const full = daysOverCap(draft, toDays, copySlotCount(draft, sIdx, slotIds));
    const targets = toDays.filter((day) => !full.includes(day));
    if (targets.length === 0) return showToast(fullNote(full));
    setDraft((d) => copySlotsToDays(d, sIdx, slotIds, targets));
    const copied = t("editorV3.copiedToDays", { days: dayCodes(targets), defaultValue: "Copied to {{days}}." });
    showToast(full.length > 0 ? `${copied} ${fullNote(full)}` : copied, undo);
  };

  // PE-40: a saved workout's exercises at the end of the day, the same work
  // in every round. The shared action reads the live cycle length.
  const importWorkout = (plan: Plan, cellUnit: UnitsPreference) =>
    setDraft((d) => appendPlanToDay(d, sIdx, plan, cellUnit));

  // #85: a day of another program at the end of the day, its prescription
  // as it is (no role values). Rounds this program lacks stay empty; rounds
  // beyond it are dropped, and the toast names them. Undo, no confirm. A day
  // with nothing in these rounds, or a full day, changes nothing: no Undo
  // then, it would take back an unrelated edit.
  const importProgramDay = (program: Program, srcIdx: number, cellUnit: UnitsPreference) => {
    const day = pickAuthoredText(program.sessions[srcIdx]?.name, lang, program.authorLocale) ?? `D${srcIdx + 1}`;
    const count = importedSlotCount(program, srcIdx, draft.cycleWeeks);
    if (count === 0) {
      return showToast(
        t("editorV3.importNothing", {
          day,
          defaultValue: "Nothing added: {{day}} has no exercise in this program's rounds.",
        }),
      );
    }
    const full = daysOverCap(draft, [sIdx], count);
    if (full.length > 0) return showToast(fullNote(full));
    setDraft((d) => importSessionSlots(d, sIdx, program, srcIdx, cellUnit));
    const dropped = droppedRoundsOf(program, srcIdx, draft.cycleWeeks);
    const name = pickAuthoredText(program.name, lang, program.authorLocale) ?? "";
    const parts = [
      t("editorV3.programDayImported", { day, program: name, defaultValue: "{{day}} of {{program}} added." }),
      dropped.length > 0
        ? t("editorV3.roundsDropped", {
            count: dropped.length,
            rounds: dropped.map((w) => programRoundLabel(w + 1)).join(", "),
            defaultValue: "{{rounds}} left out: this program has fewer rounds.",
          })
        : null,
    ];
    showToast(parts.filter(Boolean).join(" "), undo);
  };

  // The prescription stays, only the exercise changes (as prescribeDispatch's
  // replace_exercise does).
  const replace = (slotIdx: number, slug: string) =>
    setDraft((d) =>
      updateSlot(d, sIdx, slotIdx, { exerciseId: slug, exerciseName: resolveName(slug) }),
    );

  const move = (slotIdx: number, delta: -1 | 1) =>
    setDraft((d) => moveSlot(d, sIdx, slotIdx, delta));

  const remove = (slotIdx: number) => {
    const slot = draft.sessions[sIdx]?.slots[slotIdx];
    if (!slot) return;
    const name = resolveName(slot.exerciseId, slot.exerciseName);
    confirm({
      title: t("editorV3.removeExerciseTitle", { name, defaultValue: "Remove {{name}}?" }),
      body: t("editorV3.removeExerciseBody", {
        defaultValue: "It leaves this day in every round.",
      }),
      confirmLabel: t("editorV3.remove", { defaultValue: "Remove" }),
      cancelLabel: t("program.cancel", { defaultValue: "Cancel" }),
    }).then((yes) => {
      if (!yes) return;
      setDraft((d) => removeSlot(d, sIdx, slotIdx));
      nav.closeExercise();
      showToast(t("editorV3.exerciseRemoved", { name, defaultValue: "{{name}} removed" }), undo);
    });
  };

  // `align`: the author's answer when the set counts differ (ExerciseView
  // asks). Trim drops sets, so it offers Undo like a removal.
  const pairWithNext = (slotIdx: number, align: "pad" | "trim" = "pad") => {
    const counts = nextSlotJoinCounts(draft, sIdx, slotIdx);
    setDraft((d) => supersetWithNext(d, sIdx, slotIdx, align));
    if (align === "trim" && counts) {
      showToast(
        counts.sameInEveryRound
          ? t("editorV3.setsTrimmed", { n: counts.shortest, defaultValue: "Superset made, sets trimmed to {{n}}" })
          : t("editorV3.setsTrimmedEachRound", {
              defaultValue: "Superset made, sets trimmed to the shortest in each round",
            }),
        undo,
      );
    }
  };

  const renameSuperset = (groupId: string, name: string) =>
    setDraft((d) => renameGroup(d, sIdx, groupId, name));

  // Add the exercise to round `week`, or take it out (with Undo). The menu
  // disables a removal that would be refused; the toast below is the
  // fallback: the day would be empty in that round, or the exercise in none.
  const setRoundActive = (slotIdx: number, week: number, active: boolean) => {
    const res = setSlotRoundActive(draft, sIdx, slotIdx, week, active);
    const slot = draft.sessions[sIdx]?.slots[slotIdx];
    const name = slot ? resolveName(slot.exerciseId, slot.exerciseName) : "";
    const n = week + 1;
    if (!res.ok) {
      showToast(
        res.reason === "lastInDay"
          ? t("editorV3.roundLastInDay", {
              name,
              n,
              defaultValue: "{{name}} is the only exercise of this day in W{{n}}. Remove the exercise or the round instead.",
            })
          : t("editorV3.roundLastOfExercise", {
              name,
              n,
              defaultValue: "W{{n}} is the only round {{name}} runs in. Remove the exercise instead.",
            }),
      );
      return;
    }
    setDraft((d) => {
      const next = setSlotRoundActive(d, sIdx, slotIdx, week, active);
      return next.ok ? next.draft : d;
    });
    if (!active) {
      showToast(t("editorV3.removedFromRound", { name, n, defaultValue: "{{name}} removed from W{{n}}" }), undo);
    }
  };

  const dissolve = (groupId: string) =>
    setDraft((d) => applyLoggerAction(d, { type: "split_group", groupId }, { sIdx, weekIdx: 0 }));

  const setNote = (slotIdx: number, text: string) =>
    setDraft((d) => updateSlotInstructions(d, sIdx, slotIdx, text));

  // One round of one exercise, through ComposerSetTable. In a superset a set
  // is added or dropped on every member, as the card does (prescribeDispatch;
  // its program write ignores the logger's `defaultUnit`). Reached from the
  // «Per round» lens, the set count changes in this round only (#336 S1,
  // brief §15 row 23); from «Exercises», in every round, as before.
  const round = (slotIdx: number, week: number) => {
    const groupId = draft.sessions[sIdx]?.slots[slotIdx]?.group?.id;
    const ctx = { sIdx, weekIdx: week };
    const onlyThisRound = nav.lens === "round";
    return {
      onlyThisRound,
      updateRow: (rowIdx: number, patch: Partial<BlockSetRow>) =>
        setDraft((d) => applyRoundRowPatch(d, { sIdx, slotIdx, week }, rowIdx, patch)),
      setAxis: (kind: IntensityKind) => setDraft((d) => setSlotAxis(d, sIdx, slotIdx, kind, week)),
      addSet: () =>
        onlyThisRound
          ? roundSets.addSet(slotIdx, week)
          : setDraft((d) =>
              groupId
                ? applyLoggerAction(d, { type: "add_round_to_group", groupId, defaultUnit: "kg" }, ctx)
                : addSetRow(d, sIdx, slotIdx),
            ),
      removeSet: (rowIdx: number) =>
        onlyThisRound
          ? roundSets.removeSet(slotIdx, week, rowIdx)
          : setDraft((d) =>
              groupId
                ? applyLoggerAction(d, { type: "remove_round_from_group", groupId, roundIdx: rowIdx }, ctx)
                : removeSetRow(d, sIdx, slotIdx, rowIdx),
            ),
    };
  };

  return {
    add,
    copyToDays,
    importWorkout,
    importProgramDay,
    replace,
    move,
    remove,
    pairWithNext,
    renameSuperset,
    dissolve,
    setRoundActive,
    setNote,
    round,
    /** The wide grid's «Sets» lens: one round's set count (§15 row 36). */
    setRoundSets: roundSets.setCount,
    undo,
  };
}

export type V3ExerciseActions = ReturnType<typeof useV3ExerciseActions>;
